import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import AvailableTime from '../model/AvailableTime';
import { AppointmentType } from '../model/AppointmentType';
import { isBookingV2Enabled } from '../utils/bookingFeatureFlag';

type AvailabilityQueryArg = {
  specialistId?: string;
  appointmentType: AppointmentType;
  month?: string;
  /**
   * Kept so callers and the RTK Query cache key are unchanged, but no longer
   * put on the wire: v1 never sent it, and v2 now takes the duration from the
   * clinician's Schedule Settings rather than from the client.
   */
  aptLength?: string;
  locationId?: string;
};

export const availabilitiesApi = createApi({
  reducerPath: 'availabilitiesApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_BASE_URL,
    prepareHeaders: (headers) => {
      headers.set('X-API-KEY', process.env.REACT_APP_API_KEY as string);
      headers.set('Site-Id', process.env.REACT_APP_CURRENT_SITE_ID as string);
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getAvailabilitiesBySpecialist: builder.query<AvailableTime[], AvailabilityQueryArg>({
      query: ({ specialistId, appointmentType, month, locationId }) => {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        // v2: PMS-derived slots, already sliced server-side and each carrying a
        // real slotId. `duration` is deliberately NOT sent: pms-api slices by the
        // clinician's saved Schedule Settings for this appointment type, whereas
        // `aptLength` here is `appointmentLength.split('-')[0]`, i.e. always the
        // NEW duration — sending it would override the correct value on reviews.
        if (isBookingV2Enabled()) {
          return `/v2/availabilities?specialistId=${specialistId}&appointmentType=${appointmentType}${month ? `&month=${month}` : ''}&timezone=${encodeURIComponent(timezone)}${locationId ? `&locationId=${encodeURIComponent(locationId)}` : ''}`;
        }
        // v1 (default/prod): Firestore-backed availability, unchanged.
        return `/availabilities?specialistId=${specialistId}&appointmentType=${appointmentType}${month ? `&month=${month}` : ''}&timezone=${encodeURIComponent(timezone)}`;
      },
    }),
  }),
});

export const { useGetAvailabilitiesBySpecialistQuery } = availabilitiesApi;
